import React, { useEffect, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Popper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import FilledActionButton from 'components/FilledActionButton';

interface LearningStartPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onStart: (wordCount: number) => void;
  starting?: boolean;
}

const WORD_COUNT_OPTIONS = [5, 10, 15, 20];
const RECOMMENDED = 10;
// Keep in sync with BE: LearningSessionCreateRequest.MAX_WORD_COUNT.
const MAX_WORD_COUNT = 30;
const MIN_WORD_COUNT = 1;

const clampCount = (n: number) =>
  Math.max(MIN_WORD_COUNT, Math.min(MAX_WORD_COUNT, Math.trunc(n)));

const LearningStartPopover: React.FC<LearningStartPopoverProps> = ({
  anchorEl,
  open,
  onClose,
  onStart,
  starting,
}) => {
  const [count, setCount] = useState<number>(RECOMMENDED);
  // Allow the user to clear the input while typing — keeping it as a string
  // means we don't snap them back to "1" every keystroke. The committed count
  // (used by the action button) is always derived via clampCount on blur.
  const [customDraft, setCustomDraft] = useState<string>('');

  useEffect(() => {
    if (open) {
      setCount(RECOMMENDED);
      setCustomDraft('');
    }
  }, [open]);

  const pickPreset = (n: number) => {
    setCount(n);
    setCustomDraft('');
  };

  const handleCustomChange = (raw: string) => {
    setCustomDraft(raw);
    const parsed = Number(raw);
    if (Number.isFinite(parsed) && parsed > 0) {
      setCount(clampCount(parsed));
    }
  };

  const handleCustomBlur = () => {
    if (customDraft === '') return;
    const parsed = Number(customDraft);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setCustomDraft('');
      return;
    }
    const clamped = clampCount(parsed);
    setCount(clamped);
    setCustomDraft(String(clamped));
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement={'bottom-end'}
      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'primary.tableBorder',
            borderRadius: 2,
            boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            width: { xs: 320, sm: 360 },
          }}
        >
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.25}
            sx={{
              borderBottom: '1px solid',
              borderColor: 'primary.tableBorder',
              p: '14px 16px',
            }}
          >
            <PlayArrowIcon sx={{ color: 'primary.main', fontSize: 20 }} />
            <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 700 }}>
              {'Скільки слів узяти?'}
            </Typography>
            <Box
              component={'button'}
              onClick={onClose}
              sx={{
                alignItems: 'center',
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                color: 'text.secondary',
                cursor: 'pointer',
                display: 'inline-flex',
                height: 30,
                justifyContent: 'center',
                width: 30,
                '&:hover': { bgcolor: 'background.default' },
              }}
            >
              <CloseIcon sx={{ fontSize: 20 }} />
            </Box>
          </Stack>

          <Box sx={{ p: '14px 16px' }}>
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              sx={{ mb: 0.875, px: 0.25 }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 800,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                }}
              >
                {'Кількість слів'}
              </Typography>
              <Typography
                sx={{
                  color: 'text.secondary',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {`Рекомендовано · ${RECOMMENDED}`}
              </Typography>
            </Stack>
            <Stack direction={'row'} spacing={0.75}>
              {WORD_COUNT_OPTIONS.map((n) => {
                const on = count === n && customDraft === '';
                return (
                  <Box
                    key={n}
                    component={'button'}
                    onClick={() => pickPreset(n)}
                    sx={{
                      alignItems: 'center',
                      bgcolor: on ? 'primary.main' : 'background.default',
                      border: '1px solid',
                      borderColor: on ? 'primary.main' : 'primary.tableBorder',
                      borderRadius: 1.25,
                      color: on ? 'common.white' : 'text.primary',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      flex: 1,
                      fontFamily: 'inherit',
                      fontSize: 14,
                      fontWeight: 700,
                      height: 42,
                      justifyContent: 'center',
                      position: 'relative',
                      transition: 'all .14s ease',
                    }}
                  >
                    {n}
                  </Box>
                );
              })}
            </Stack>

            <Stack
              direction={'row'}
              alignItems={'center'}
              spacing={1.25}
              sx={{ mt: 1.5 }}
            >
              <Typography
                sx={{
                  color: 'text.secondary',
                  flex: 1,
                  fontSize: 13.5,
                  fontWeight: 600,
                }}
              >
                {'Або своє число'}
              </Typography>
              <TextField
                type={'number'}
                size={'small'}
                value={customDraft}
                placeholder={String(count)}
                onChange={(e) => handleCustomChange(e.target.value)}
                onBlur={handleCustomBlur}
                inputProps={{
                  min: MIN_WORD_COUNT,
                  max: MAX_WORD_COUNT,
                  step: 1,
                  inputMode: 'numeric',
                  style: { textAlign: 'center', fontWeight: 700 },
                }}
                sx={{
                  width: 96,
                  '& .MuiOutlinedInput-root': { height: 38 },
                }}
              />
            </Stack>

            <Typography
              sx={{
                color: 'text.secondary',
                fontSize: 12.5,
                lineHeight: 1.45,
                mt: 1.25,
              }}
            >
              {`Можна обрати від ${MIN_WORD_COUNT} до ${MAX_WORD_COUNT} слів. Якщо в словнику менше — сесія візьме всі доступні.`}
            </Typography>
          </Box>

          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            spacing={1}
            sx={{
              borderTop: '1px solid',
              borderColor: 'primary.tableBorder',
              p: '12px 16px',
            }}
          >
            <FilledActionButton
              startIcon={<PlayArrowIcon />}
              onClick={() => onStart(count)}
              disabled={starting}
              sx={{ fontSize: 14, height: 38, px: 2 }}
            >
              {'Почати'}
            </FilledActionButton>
          </Stack>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};

export default LearningStartPopover;
